import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  VendorProduct,
  VendorProductsService,
} from 'src/app/services/vendorproducts/vendorproducts.service';
import { jsonValidator } from 'src/app/validators/json-validator';

@Component({
    selector: 'app-vendor-product-form',
    templateUrl: './vendor-product-form.component.html',
    styleUrls: ['./vendor-product-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VendorProductFormComponent {
  @Input() set product(value: VendorProduct) {
    if (value) {
      this._product = value;
      this.populateForm();
    }
  }
  @Output() productSaved = new EventEmitter<void>();
  private _product!: VendorProduct;
  public productForm!: FormGroup;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly snackBar: MatSnackBar,
    private readonly vendorProductsService: VendorProductsService,
  ) {
    this.initializeForm();
  }

  private initializeForm(): void {
    this.productForm = this.formBuilder.group({
      vendorName: ['', Validators.required],
      productName: ['', Validators.required],
      displayName: [''],
      vendorSlug: [''],
      productSlug: [''],
      stripeProductId: [''],
      stripeVendor: [''],
      metadata: ['', [jsonValidator, (control: any) => {
        try {
          const value = JSON.parse(control.value || '{}');
          return value && typeof value === 'object' && !Array.isArray(value) ? null : { invalidMetadata: true };
        } catch { return null; }
      }]],
      feedConfig: ['', [jsonValidator]],
      billingCycle: [''],
    });
    // Keep the dedicated field and the advanced JSON editor in sync.
    this.productForm.get('metadata')!.valueChanges.subscribe(value => {
      if (this.productForm.get('metadata')!.invalid) return;
      const metadata = JSON.parse(value || '{}');
      this.productForm.get('displayName')!.setValue(
        typeof metadata.displayName === 'string' ? metadata.displayName : '', { emitEvent: false },
      );
    });
    this.productForm.get('displayName')!.valueChanges.subscribe(value => {
      const metadataControl = this.productForm.get('metadata')!;
      if (metadataControl.invalid) return;
      const metadata = JSON.parse(metadataControl.value || '{}');
      const displayName = (value || '').trim();
      if (displayName) metadata.displayName = displayName;
      else delete metadata.displayName;
      metadataControl.setValue(JSON.stringify(metadata, null, 2), { emitEvent: false });
    });
  }

  private populateForm(): void {
    if (this._product) {
      this.productForm.patchValue({
        vendorName: this._product.vendorName || '',
        productName: this._product.productName || '',
        vendorSlug: this._product.vendorSlug || '',
        productSlug: this._product.productSlug || '',
        stripeProductId: this._product.stripeProductId || '',
        stripeVendor: this._product.stripeVendor || '',
        metadata: JSON.stringify(this._product.metadata || {}, null, 2),
        feedConfig: JSON.stringify(this._product.feedConfig || {}, null, 2),
      });
    }
  }

  public formatJsonOnBlur(controlName: string): void {
    const ctrl = this.productForm.get(controlName);
    if (!ctrl) return;

    const value = ctrl.value;

    try {
      const parsed = JSON.parse(value);
      const formatted = JSON.stringify(parsed, null, 2);

      // Only update if formatting changed (prevents cursor jump for nothing)
      if (formatted !== value) {
        ctrl.setValue(formatted, { emitEvent: false });
      }
    } catch {
      // Do nothing — validation already handles invalid JSON
    }
  }

  public async saveProduct(): Promise<void> {
    if (this.productForm.valid) {
      const { displayName, ...formValue } = this.productForm.value;

      try {
        // Parse JSON fields
        const productData = {
          ...formValue,
          metadata: JSON.parse(formValue.metadata || '{}'),
          feedConfig: JSON.parse(formValue.feedConfig || '{}'),
        };

        const updateResponse = await this.vendorProductsService.putVendorProduct(
          this._product.id,
          productData,
        );
        if (!updateResponse.success) {
          this.snackBar.open(
            updateResponse.message || updateResponse.error || 'Error saving product details',
            'Error',
            {
              duration: 3000,
            },
          );
          return;
        }
        this.productSaved.emit();
      } catch (error) {
        this.snackBar.open('Error saving product details', 'Error', {
          duration: 3000,
        });
      }
    }
  }

  public resetForm(): void {
    this.populateForm();
  }
}
